"use client"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { useState } from "react"
import { Loader } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Card } from "./ui/card"
import { Textarea } from "./ui/textarea"
import { Popup } from "./ui/popup"
import { BLOG_URL } from "@/constants/constants"

const formSchema = z.object({
    name: z
        .string()
        .min(3, { message: "Name must be at least 3 characters." })
        .nonempty({ message: "Name is required." }),
    email: z
        .email({ message: "Please provide a valid email address." })
        .nonempty({ message: "Email is required." }),
    message: z
        .string()
        .min(10, { message: "Message must be at least 10 characters." })
        .nonempty({ message: "Message is required." }),
});

const ContactForm = () => {
    const [isLoading, setIsLoading] = useState(false)
    const [popup, setPopup] = useState<{
        isOpen: boolean
        type: "success" | "error"
        title: string
        message: string
    }>({
        isOpen: false,
        type: "success",
        title: "",
        message: ""
    });
    const navigateToBlogs = () => {
        window.location.href = BLOG_URL;
    }

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: "",
            email: "",
            message: ""
        },
    })

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        setIsLoading(true)
        try {
            const res = await fetch("/api/send-email", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(values),
            });

            if (!res.ok) throw new Error("Failed to send message");

            setPopup({
                isOpen: true,
                type: "success",
                title: "Success!",
                message: "Your message has been sent successfully. I'll get back to you soon!"
            });
            form.reset();
        } catch (error) {
            console.log(error);
            setPopup({
                isOpen: true,
                type: "error",
                title: "Error",
                message: "Failed to send message. Please try again later or contact me directly via email."
            });
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <>
            <Card className="w-full lg:w-[500px] p-6">
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col space-y-8">
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Name</FormLabel>
                                    <FormControl>
                                        <Input placeholder="John Doe" {...field} />
                                    </FormControl>

                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Email</FormLabel>
                                    <FormControl>
                                        <Input placeholder="example@gmail.com" {...field} />
                                    </FormControl>

                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="message"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Message</FormLabel>
                                    <FormControl>
                                        <Textarea placeholder="Write your meesage here..." {...field} />
                                    </FormControl>

                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <Button
                            type="submit"
                            variant={"outline"}
                            className="mt-2 ml-auto"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <Loader className="h-4 w-4 animate-spin" />
                                    Sending...
                                </>
                            ) : (
                                "Send Message"
                            )}
                        </Button>
                    </form>
                </Form>
            </Card>
            <Popup
                isOpen={popup.isOpen}
                onClose={() => setPopup({ ...popup, isOpen: false })}
                type={popup.type}
                title={popup.title}
                message={popup.message}
                primaryButtonLabel="OK"
                secondaryButtonLabel="View Blog"
                onSecondaryClick={navigateToBlogs}
            />
        </>
    )
}

export default ContactForm